import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Apple, Calculator, Utensils, Save, RefreshCw, Plus, FileText, Trash2, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { FOOD_DATABASE } from '../data/foodDatabase';
import {
  calculateProteinPortion,
  calculateSmartFat,
  calculatePortionForSelect,
  filterFoodsByPreferences,
  filterFoodsByDietType,
  roundGramsForDisplay,
  getFoodById
} from '../utils/nutritionCalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_MENUS = 5;

const NutritionFormLevel2 = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingForm, setExistingForm] = useState(null);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [showPlanBuilder, setShowPlanBuilder] = useState(false);
  const [selectedFoods, setSelectedFoods] = useState({});
  const [savedMenus, setSavedMenus] = useState([]);
  const [menusCreatedThisMonth, setMenusCreatedThisMonth] = useState(0);
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(null);

  const [formData, setFormData] = useState({
    nombre_completo: '',
    correo_electronico: '',
    sexo: 'mujer',
    edad: '',
    estatura: '',
    peso: '',
    nivel_actividad: 'moderado',
    objetivo: 'mantener',
    alergias: '',
    alimentos_no_gustan: '',
    lesiones_impedimentos: '',
    numero_comidas: '5',
    tipo_alimentacion: 'ninguna'
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userRes.data);

      setFormData(prev => ({
        ...prev,
        nombre_completo: userRes.data.name || '',
        correo_electronico: userRes.data.email || ''
      }));

      // Obtener menús guardados del usuario
      const menusRes = await axios.get(`${BACKEND_URL}/api/nutrition-form/my-menus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (menusRes.data) {
        setSavedMenus(menusRes.data.menus || []);
        setMenusCreatedThisMonth(menusRes.data.menus_this_month || 0);
        if (menusRes.data.nutrition_plan) {
          setNutritionPlan(menusRes.data.nutrition_plan);
          setShowPlanBuilder(true);
        }
        if (menusRes.data.form_data) {
          setExistingForm(menusRes.data.form_data);
        }
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const calculateHarrisBenedict = (peso, estatura, edad, sexo) => {
    if (sexo === 'hombre') {
      return 66.5 + (13.75 * peso) + (5.003 * estatura) - (6.755 * edad);
    } else {
      return 655.1 + (9.563 * peso) + (1.850 * estatura) - (4.676 * edad);
    }
  };

  const getActivityFactor = (nivel) => {
    const factors = {
      sedentario: 1.2,
      ligero: 1.375,
      moderado: 1.55,
      activo: 1.725,
      muy_activo: 1.9
    };
    return factors[nivel] || 1.55;
  };

  const getObjectiveAdjustment = (objetivo) => {
    const adjustments = {
      perder: -500,
      mantener: 0,
      ganar: 300
    };
    return adjustments[objetivo] || 0;
  };

  const filterFoods = (foods) => {
    const byPreferences = filterFoodsByPreferences(foods, formData.alergias, formData.alimentos_no_gustan);
    return filterFoodsByDietType(byPreferences, formData.tipo_alimentacion);
  };

  const handleFoodSelect = (mealIndex, category, foodId) => {
    setSelectedFoods(prev => ({
      ...prev,
      [`meal_${mealIndex}_${category}`]: foodId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre_completo || !formData.correo_electronico || !formData.edad || 
        !formData.estatura || !formData.peso || !formData.numero_comidas) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('session_token');

    try {
      const geb = calculateHarrisBenedict(
        parseFloat(formData.peso),
        parseFloat(formData.estatura),
        parseInt(formData.edad),
        formData.sexo
      );
      
      const activityFactor = getActivityFactor(formData.nivel_actividad);
      const tdee = geb * activityFactor;
      const objectiveAdjustment = getObjectiveAdjustment(formData.objetivo);
      const caloriasDiarias = Math.round(tdee + objectiveAdjustment);

      const proteinas = Math.round((caloriasDiarias * 0.30) / 4);
      const carbohidratos = Math.round((caloriasDiarias * 0.40) / 4);
      const grasas = Math.round((caloriasDiarias * 0.30) / 9);

      const numComidas = parseInt(formData.numero_comidas);

      const nutritionPlanData = {
        geb: Math.round(geb),
        tdee: Math.round(tdee),
        calorias_diarias: caloriasDiarias,
        macros: {
          proteinas_g: proteinas,
          carbohidratos_g: carbohidratos,
          grasas_g: grasas
        },
        comidas_por_dia: numComidas,
        calorias_por_comida: Math.round(caloriasDiarias / numComidas),
        macros_por_comida: {
          proteinas: Math.round(proteinas / numComidas),
          carbohidratos: Math.round(carbohidratos / numComidas),
          grasas: Math.round(grasas / numComidas)
        }
      };

      // Guardar datos del formulario
      await axios.post(`${BACKEND_URL}/api/nutrition-form/submit-level2`, {
        ...formData,
        nutrition_plan: nutritionPlanData,
        level: 'nivel_2'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('¡Información guardada! Ahora puedes crear tus menús.');
      setNutritionPlan(nutritionPlanData);
      setShowPlanBuilder(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el formulario');
    } finally {
      setSubmitting(false);
    }
  };

  const generateMenu = async () => {
    if (menusCreatedThisMonth >= MAX_MENUS) {
      toast.error('Has llegado al límite de menús este mes');
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      const menuData = {
        name: `Menú ${savedMenus.length + 1}`,
        selected_foods: selectedFoods,
        created_at: new Date().toISOString()
      };

      const response = await axios.post(`${BACKEND_URL}/api/nutrition-form/save-menu`, {
        menu: menuData,
        level: 'nivel_2'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedMenus([...savedMenus, { ...menuData, id: response.data.menu_id }]);
      setMenusCreatedThisMonth(menusCreatedThisMonth + 1);
      setSelectedFoods({});
      toast.success('¡Menú guardado exitosamente!');
    } catch (error) {
      toast.error('Error al guardar el menú');
    }
  };

  const deleteMenu = async (menuId, index) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/nutrition-form/delete-menu/${menuId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newMenus = savedMenus.filter((_, i) => i !== index);
      setSavedMenus(newMenus);
      toast.success('Menú eliminado');
    } catch (error) {
      toast.error('Error al eliminar el menú');
    }
  };

  const loadMenu = (menu, index) => {
    setSelectedFoods(menu.selected_foods || {});
    setSelectedMenuIndex(index);
    toast.info(`Cargado: ${menu.name}`);
  };

  const getSelectedFoodDetails = (mealIndex, category, foods) => {
    const foodId = foods[`meal_${mealIndex}_${category}`];
    return getFoodById(category, foodId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  // Vista del constructor de planes
  if (showPlanBuilder && nutritionPlan) {
    const plan = nutritionPlan;
    const canCreateMore = menusCreatedThisMonth < MAX_MENUS;
    
    return (
      <div className="min-h-screen bg-[#09090b] py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center text-zinc-400 hover:text-pink-500 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Dashboard
            </button>
            
            <h1 className="text-3xl font-bold mb-2 text-white">Plan Nutricional Nivel 2 - Intermedio</h1>
            <p className="text-zinc-400">
              Crea y guarda hasta 5 menús diferentes para variar tu alimentación
            </p>
          </div>

          {/* Layout de 2 columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Columna principal - Constructor de menú */}
            <div className="lg:col-span-2 space-y-6">
              {/* Resumen de calorías */}
              <div className="bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-2xl p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Calculator className="w-6 h-6" />
                  Tu Requerimiento Calórico Diario
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold">{plan.calorias_diarias}</div>
                    <div className="text-sm opacity-90">Calorías totales</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold">{plan.macros.proteinas_g}g</div>
                    <div className="text-sm opacity-90">Proteínas</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold">{plan.macros.carbohidratos_g}g</div>
                    <div className="text-sm opacity-90">Carbohidratos</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold">{plan.macros.grasas_g}g</div>
                    <div className="text-sm opacity-90">Grasas</div>
                  </div>
                </div>
              </div>

              {/* Constructor de menú */}
              <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold flex items-center gap-2 text-pink-500">
                    <Utensils className="w-7 h-7" />
                    Crear Nuevo Menú
                  </h2>
                  {selectedMenuIndex !== null && (
                    <span className="bg-pink-500/20 text-pink-400 px-3 py-1 rounded-full text-sm">
                      Editando: Menú {selectedMenuIndex + 1}
                    </span>
                  )}
                </div>
                
                <p className="text-zinc-400 mb-6">
                  Selecciona los alimentos para cada una de tus {plan.comidas_por_dia} comidas del día.
                </p>

                {/* Comidas */}
                <div className="space-y-6">
                  {Array.from({ length: plan.comidas_por_dia }, (_, mealIndex) => {
                    const getMealName = (idx) => {
                      if (idx === 0) return 'Desayuno';
                      if (idx === 1) return 'Media Mañana';
                      if (idx === 2) return 'Almuerzo';
                      if (idx === 3) return 'Post Entreno';
                      return 'Cena';
                    };
                    
                    const hasFruit = mealIndex === 0 || mealIndex === 1 || mealIndex === 3;
                    const proteinPerMeal = Math.round(plan.macros.proteinas_g / plan.comidas_por_dia);
                    const carbsPerMeal = Math.round(plan.macros.carbohidratos_g / plan.comidas_por_dia);
                    const fatsPerMeal = Math.round(plan.macros.grasas_g / plan.comidas_por_dia);
                    
                    return (
                      <div key={mealIndex} className="border border-zinc-700 rounded-xl p-4 bg-zinc-800/50">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 ${mealIndex === 3 ? 'bg-orange-500/20' : 'bg-pink-500/20'} rounded-full flex items-center justify-center`}>
                            <span className={`font-bold ${mealIndex === 3 ? 'text-orange-400' : 'text-pink-400'}`}>{mealIndex + 1}</span>
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{getMealName(mealIndex)}</h3>
                            <p className="text-xs text-zinc-500">
                              P: {proteinPerMeal}g | C: {carbsPerMeal}g | G: {fatsPerMeal}g
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {/* Proteína */}
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                            <label className="block text-xs font-bold text-blue-400 mb-1">🥩 Proteína ({proteinPerMeal}g)</label>
                            <select
                              value={selectedFoods[`meal_${mealIndex}_proteina`] || ''}
                              onChange={(e) => handleFoodSelect(mealIndex, 'proteina', e.target.value)}
                              className="w-full p-2 border border-zinc-700 rounded-lg bg-zinc-900 text-white text-sm"
                            >
                              <option value="">Selecciona...</option>
                              {filterFoods(FOOD_DATABASE.proteinas).map(food => {
                                const portion = calculatePortionForSelect(food, proteinPerMeal, 'proteinas');
                                return (
                                  <option key={food.id} value={food.id}>
                                    {food.nombre} - {portion.displayGrams}g ({food.medicion})
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {/* Carbohidrato */}
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                            <label className="block text-xs font-bold text-amber-400 mb-1">🍚 Carbohidrato ({hasFruit ? Math.max(carbsPerMeal - 15, 10) : carbsPerMeal}g)</label>
                            <select
                              value={selectedFoods[`meal_${mealIndex}_carbohidrato`] || ''}
                              onChange={(e) => handleFoodSelect(mealIndex, 'carbohidrato', e.target.value)}
                              className="w-full p-2 border border-zinc-700 rounded-lg bg-zinc-900 text-white text-sm"
                            >
                              <option value="">Selecciona...</option>
                              {filterFoods(FOOD_DATABASE.carbohidratos).map(food => {
                                const adjustedCarbs = hasFruit ? Math.max(carbsPerMeal - 15, 10) : carbsPerMeal;
                                const portion = calculatePortionForSelect(food, adjustedCarbs, 'carbohidratos');
                                return (
                                  <option key={food.id} value={food.id}>
                                    {food.nombre} - {portion.displayGrams}g ({food.medicion})
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {/* Grasa Inteligente */}
                          {(() => {
                            const selectedProteinId = selectedFoods[`meal_${mealIndex}_proteina`];
                            const proteinFood = getFoodById('proteina', selectedProteinId);
                            let smartFatInfo = { needsAdditionalFat: true, remainingFat: fatsPerMeal };
                            
                            if (proteinFood) {
                              const proteinCalc = calculateProteinPortion(proteinFood, proteinPerMeal);
                              smartFatInfo = calculateSmartFat(fatsPerMeal, proteinCalc.fatContributed);
                            }
                            
                            return (
                              <div className={`rounded-lg p-3 ${!smartFatInfo.needsAdditionalFat ? 'bg-green-900/30 border border-green-500/50' : 'bg-green-500/10 border border-green-500/30'}`}>
                                <label className="block text-xs font-bold text-green-400 mb-1">
                                  🥑 Grasa {smartFatInfo.needsAdditionalFat ? `(${Math.round(smartFatInfo.remainingFat)}g faltante)` : '✓'}
                                </label>
                                {!smartFatInfo.needsAdditionalFat ? (
                                  <div className="flex items-center gap-2 text-green-400 p-2 bg-green-900/20 rounded-lg text-xs">
                                    <CheckCircle className="w-4 h-4" />
                                    <span>Sin grasa adicional - la proteína aporta suficiente</span>
                                  </div>
                                ) : (
                                  <select
                                    value={selectedFoods[`meal_${mealIndex}_grasa`] || ''}
                                    onChange={(e) => handleFoodSelect(mealIndex, 'grasa', e.target.value)}
                                    className="w-full p-2 border border-zinc-700 rounded-lg bg-zinc-900 text-white text-sm"
                                  >
                                    <option value="">Selecciona...</option>
                                    {filterFoods(FOOD_DATABASE.grasas).map(food => {
                                      const portion = calculatePortionForSelect(food, smartFatInfo.remainingFat, 'grasas');
                                      return (
                                        <option key={food.id} value={food.id}>
                                          {food.nombre} - {Math.round(portion.grams)}g
                                        </option>
                                      );
                                    })}
                                  </select>
                                )}
                              </div>
                            );
                          })()}

                          {/* Fruta */}
                          {hasFruit && (
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                              <label className="block text-xs font-bold text-purple-400 mb-1">🍎 Fruta (~15g carbs)</label>
                              <select
                                value={selectedFoods[`meal_${mealIndex}_fruta`] || ''}
                                onChange={(e) => handleFoodSelect(mealIndex, 'fruta', e.target.value)}
                                className="w-full p-2 border border-zinc-700 rounded-lg bg-zinc-900 text-white text-sm"
                              >
                                <option value="">Selecciona...</option>
                                {filterFoods(FOOD_DATABASE.frutas).map(food => {
                                  const portion = calculatePortionForSelect(food, 15, 'carbohidratos');
                                  return (
                                    <option key={food.id} value={food.id}>
                                      {food.nombre} - {portion.displayGrams}g
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          )}
                          
                          {/* Vegetales */}
                          <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3 col-span-2">
                            <label className="block text-xs font-bold text-emerald-400 mb-1">🥦 Vegetales (Libre)</label>
                            <p className="text-emerald-300 text-xs">Sugerido: 100-200g (no cuenta en macros)</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Botón Generar Menú */}
                <div className="mt-6">
                  {canCreateMore ? (
                    <>
                      <Button
                        onClick={generateMenu}
                        className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6 font-bold text-lg"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Generar Menú
                      </Button>
                      <div className="mt-3 bg-pink-500/10 border border-pink-500/30 rounded-xl p-4 text-center">
                        <p className="text-pink-300 text-sm">
                          <strong>💡 Con este plan, tienes la opción de cambiar 5 veces tu plan nutricional.</strong>
                          <br />¡Elige tu menú favorito!
                        </p>
                        <p className="text-pink-400 text-xs mt-2">
                          Menús creados este mes: {menusCreatedThisMonth} de {MAX_MENUS}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
                      <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                      <p className="text-amber-400 font-bold text-lg">
                        Llegaste al límite de creaciones por mes
                      </p>
                      <p className="text-amber-500 text-sm mt-2">
                        Por favor espera al mes siguiente para crear nuevos menús.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Columna lateral - Mis Planes */}
            <div className="lg:col-span-1">
              <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 sticky top-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-white">
                  <FileText className="w-6 h-6 text-pink-500" />
                  Mis Planes
                </h3>
                
                {savedMenus.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aún no tienes menús guardados</p>
                    <p className="text-sm mt-1">Crea tu primer menú seleccionando los alimentos</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {savedMenus.map((menu, index) => (
                      <div 
                        key={menu.id || index}
                        className={`border rounded-xl p-4 transition-all cursor-pointer ${
                          selectedMenuIndex === index 
                            ? 'border-pink-500 bg-pink-500/10' 
                            : 'border-zinc-700 hover:border-pink-500/50 bg-zinc-800/50'
                        }`}
                        onClick={() => loadMenu(menu, index)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-bold text-white">{menu.name}</h4>
                            <p className="text-xs text-zinc-500">
                              {new Date(menu.created_at).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'short'
                              })}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('¿Eliminar este menú?')) {
                                deleteMenu(menu.id, index);
                              }
                            }}
                            className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-zinc-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Menús usados:</span>
                    <span className="font-bold text-pink-500">{menusCreatedThisMonth} / {MAX_MENUS}</span>
                  </div>
                  <div className="w-full bg-zinc-700 rounded-full h-2 mt-2">
                    <div 
                      className="bg-pink-500 h-2 rounded-full transition-all"
                      style={{ width: `${(menusCreatedThisMonth / MAX_MENUS) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Vista del menú seleccionado */}
                {selectedMenuIndex !== null && savedMenus[selectedMenuIndex] && (
                  <div className="mt-6 pt-4 border-t border-zinc-700">
                    <h4 className="text-lg font-bold text-pink-500 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      {savedMenus[selectedMenuIndex].name}
                    </h4>
                    <div className="space-y-4">
                      {(() => {
                        const menu = savedMenus[selectedMenuIndex];
                        // Buscar en ambos formatos: selected_foods y foods
                        const foods = menu.selected_foods || menu.foods || {};
                        const foodEntries = Object.entries(foods);
                        
                        // Obtener macros por comida del plan nutricional
                        const macrosPerMeal = menu.nutrition_plan?.macros_por_comida || {
                          proteinas: 34,
                          carbohidratos: 45,
                          grasas: 15
                        };
                        
                        if (foodEntries.length === 0) {
                          return (
                            <p className="text-zinc-500 text-sm text-center py-4">
                              Este menú no tiene alimentos guardados
                            </p>
                          );
                        }
                        
                        // Agrupar alimentos por comida
                        const mealGroups = {};
                        foodEntries.forEach(([key, value]) => {
                          const match = key.match(/meal_(\d+)_/);
                          if (match) {
                            const mealIndex = parseInt(match[1]);
                            if (!mealGroups[mealIndex]) mealGroups[mealIndex] = {};
                            mealGroups[mealIndex][key] = value;
                          }
                        });
                        
                        // Función para calcular gramos
                        const calcGrams = (food, targetMacro, macroType) => {
                          if (!food) return 0;
                          const macroPer100g = food[macroType] || 0;
                          if (macroPer100g <= 0) return 0;
                          return Math.round((targetMacro / macroPer100g) * 100);
                        };
                        
                        return Object.entries(mealGroups).map(([mealIndex, mealFoods]) => {
                          // Buscar con ambos nombres de claves (español e inglés)
                          const proteinId = mealFoods[`meal_${mealIndex}_protein`] || mealFoods[`meal_${mealIndex}_proteina`];
                          const carbId = mealFoods[`meal_${mealIndex}_carbs`] || mealFoods[`meal_${mealIndex}_carbohidrato`] || mealFoods[`meal_${mealIndex}_carbo`];
                          const fatId = mealFoods[`meal_${mealIndex}_fat`] || mealFoods[`meal_${mealIndex}_grasa`];
                          const fruitId = mealFoods[`meal_${mealIndex}_fruit`] || mealFoods[`meal_${mealIndex}_fruta`];
                          
                          // Usar categorías en español para getFoodById
                          const protein = getFoodById('proteina', proteinId);
                          const carb = getFoodById('carbohidrato', carbId);
                          const fat = getFoodById('grasa', fatId);
                          const fruit = getFoodById('fruta', fruitId);
                          
                          // Calcular gramos para cada alimento
                          const proteinGrams = calcGrams(protein, macrosPerMeal.proteinas, 'proteinas');
                          const carbGrams = calcGrams(carb, macrosPerMeal.carbohidratos, 'carbohidratos');
                          const fatGrams = macrosPerMeal.grasas; // Grasas se miden en gramos directamente
                          const fruitGrams = fruit ? calcGrams(fruit, 15, 'carbohidratos') : 0; // ~15g carbos por fruta
                          
                          return (
                            <div key={mealIndex} className="bg-zinc-800/50 rounded-xl p-3">
                              <div className="text-sm font-semibold text-zinc-300 mb-2">
                                Comida {parseInt(mealIndex) + 1}
                              </div>
                              <div className="grid grid-cols-1 gap-1.5 text-xs">
                                {protein && (
                                  <div className="flex items-center justify-between text-emerald-400">
                                    <span className="flex items-center gap-1">
                                      <span>🥩</span>
                                      <span>{protein.nombre || protein.name}</span>
                                    </span>
                                    <span className="text-zinc-400">{proteinGrams}g</span>
                                  </div>
                                )}
                                {carb && (
                                  <div className="flex items-center justify-between text-amber-400">
                                    <span className="flex items-center gap-1">
                                      <span>🍚</span>
                                      <span>{carb.nombre || carb.name}</span>
                                    </span>
                                    <span className="text-zinc-400">{carbGrams}g</span>
                                  </div>
                                )}
                                {fat && (
                                  <div className="flex items-center justify-between text-yellow-400">
                                    <span className="flex items-center gap-1">
                                      <span>🥑</span>
                                      <span>{fat.nombre || fat.name}</span>
                                    </span>
                                    <span className="text-zinc-400">{fatGrams}g</span>
                                  </div>
                                )}
                                {fruit && (
                                  <div className="flex items-center justify-between text-pink-400">
                                    <span className="flex items-center gap-1">
                                      <span>🍎</span>
                                      <span>{fruit.nombre || fruit.name}</span>
                                    </span>
                                    <span className="text-zinc-400">{fruitGrams}g</span>
                                  </div>
                                )}
                                {/* Vegetales siempre se muestran */}
                                <div className="flex items-center justify-between text-green-400">
                                  <span className="flex items-center gap-1">
                                    <span>🥬</span>
                                    <span>Vegetales</span>
                                  </span>
                                  <span className="text-zinc-400">100-200g</span>
                                </div>
                                {!protein && !carb && !fat && !fruit && (
                                  <span className="text-zinc-500">Sin alimentos definidos</span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    <button
                      onClick={() => setSelectedMenuIndex(null)}
                      className="mt-4 w-full text-center text-sm text-zinc-400 hover:text-pink-500"
                    >
                      Cerrar vista
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista del formulario inicial
  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center text-zinc-400 hover:text-pink-500 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </button>
          
          <h1 className="text-3xl font-bold mb-2 text-white">Formulario de Ingreso</h1>
          <p className="text-pink-500 font-medium">Plan Nutricional Nivel 2 - Intermedio</p>
        </div>

        {/* Nota informativa */}
        <div className="bg-pink-500/10 border border-pink-500/30 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Apple className="w-6 h-6 text-pink-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-pink-400 mb-2">NOTA IMPORTANTE</h3>
              <p className="text-zinc-300 text-sm leading-relaxed">
                El objetivo de este plan es ayudarte a seguir consolidando un estilo de vida saludable y llevarte a un siguiente nivel. Por eso, hemos diseñado un plan de entrenamiento de 5 días y un programa de nutrición enfocado en optimizar tu rendimiento y avanzar en tu recomposición corporal.
              </p>
              <p className="text-zinc-300 text-sm leading-relaxed mt-3">
                Al encontrarte en un nivel intermedio, tu cuerpo ya tiene una base y ha creado un hábito, lo que nos permite trabajar con mayor enfoque. Esto significa que podrás seguir reduciendo grasa, desarrollando masa muscular y mejorando tu forma física de manera progresiva y sostenible.
              </p>
            </div>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-3xl p-6 border border-zinc-800 space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              1. Nombre completo <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.nombre_completo}
              onChange={(e) => handleChange('nombre_completo', e.target.value)}
              placeholder="Tu nombre completo"
              className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              2. Correo electrónico <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={formData.correo_electronico}
              onChange={(e) => handleChange('correo_electronico', e.target.value)}
              placeholder="tu@email.com"
              className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              3. Sexo <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                <input
                  type="radio"
                  name="sexo"
                  value="mujer"
                  checked={formData.sexo === 'mujer'}
                  onChange={(e) => handleChange('sexo', e.target.value)}
                  className="w-4 h-4 text-pink-600"
                />
                <span>Mujer</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
                <input
                  type="radio"
                  name="sexo"
                  value="hombre"
                  checked={formData.sexo === 'hombre'}
                  onChange={(e) => handleChange('sexo', e.target.value)}
                  className="w-4 h-4 text-pink-600"
                />
                <span>Hombre</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-300">
                4. Edad <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                value={formData.edad}
                onChange={(e) => handleChange('edad', e.target.value)}
                placeholder="25"
                className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                min="15"
                max="100"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-300">
                5. Estatura (cm) <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                value={formData.estatura}
                onChange={(e) => handleChange('estatura', e.target.value)}
                placeholder="165"
                className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                min="100"
                max="250"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-zinc-300">
                6. Peso (kg) <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                step="0.1"
                value={formData.peso}
                onChange={(e) => handleChange('peso', e.target.value)}
                placeholder="60"
                className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                min="30"
                max="300"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              7. Nivel de actividad física <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.nivel_actividad}
              onChange={(e) => handleChange('nivel_actividad', e.target.value)}
              className="w-full px-4 py-2 border rounded-xl bg-zinc-800 border-zinc-700 text-white"
              required
            >
              <option value="sedentario">Sedentario (poco o nada de ejercicio)</option>
              <option value="ligero">Ligero (ejercicio 1-3 días/semana)</option>
              <option value="moderado">Moderado (ejercicio 3-5 días/semana)</option>
              <option value="activo">Activo (ejercicio 6-7 días/semana)</option>
              <option value="muy_activo">Muy activo (atleta o trabajo físico intenso)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              8. Objetivo principal <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.objetivo}
              onChange={(e) => handleChange('objetivo', e.target.value)}
              className="w-full px-4 py-2 border rounded-xl bg-zinc-800 border-zinc-700 text-white"
              required
            >
              <option value="perder">Perder grasa (déficit calórico)</option>
              <option value="mantener">Mantener peso (recomposición)</option>
              <option value="ganar">Ganar masa muscular (superávit calórico)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              9. ¿Sigues algun tipo de alimentacion especial?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'ninguna', label: 'Ninguna', desc: 'Todos los alimentos' },
                { value: 'vegetariano', label: 'Vegetariano', desc: 'Sin carne ni pescado' },
                { value: 'vegano', label: 'Vegano estricto', desc: 'Solo alimentos vegetales' },
                { value: 'pescetariano', label: 'Pescetariano', desc: 'Sin carne roja ni pollo' }
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => handleChange('tipo_alimentacion', opt.value)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    formData.tipo_alimentacion === opt.value
                      ? 'border-green-500 bg-green-500/10 text-white'
                      : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-500'
                  }`}
                  data-testid={`diet-type-${opt.value}`}
                >
                  <p className="font-bold text-sm">{opt.label}</p>
                  <p className="text-xs opacity-70">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              10. Alergias (alimentos y medicamentos)
            </label>
            <textarea
              value={formData.alergias}
              onChange={(e) => handleChange('alergias', e.target.value)}
              placeholder="Describe tus alergias alimentarias o a medicamentos"
              rows={2}
              className="w-full px-4 py-2 border rounded-xl resize-none bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              11. Alimentos que NO te gustan
            </label>
            <textarea
              value={formData.alimentos_no_gustan}
              onChange={(e) => handleChange('alimentos_no_gustan', e.target.value)}
              placeholder="Ej: brócoli, pescado, hígado (separados por coma)"
              rows={2}
              className="w-full px-4 py-2 border rounded-xl resize-none bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-300">
              12. Número de comidas al día <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.numero_comidas}
              onChange={(e) => handleChange('numero_comidas', e.target.value)}
              className="w-full px-4 py-2 border rounded-xl bg-zinc-800 border-zinc-700 text-white"
              required
            >
              <option value="4">4 comidas</option>
              <option value="5">5 comidas (recomendado)</option>
              <option value="6">6 comidas</option>
            </select>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6 font-bold"
          >
            {submitting ? 'Calculando...' : (
              <>
                <Calculator className="w-5 h-5 mr-2" />
                Calcular y Crear Mis Menús
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default NutritionFormLevel2;
